import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Loader2, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { CATEGORIES, REVIEW_PERIODS, articleProblem, createPayload, draftPayload, emptyArticleForm, emptySection, knowledgeError, sectionsPayload } from "../lib/knowledge";
import { Sections } from "../components/knowledge/ArticleText";
import { Button } from "../components/ui/button";
import { Card } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Skeleton } from "../components/ui/skeleton";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";

// Writing a new article, or the draft of an existing one. Readers never see a draft: it only replaces the published version on « Publier ».
export default function KnowledgeArticleEditor() {
  const { articleId } = useParams();
  const editing = !!articleId;
  const navigate = useNavigate();
  const [form, setForm] = useState(() => emptyArticleForm());
  const [source, setSource] = useState(null);
  const [owners, setOwners] = useState([]);
  const [phase, setPhase] = useState(editing ? "loading" : "ready");
  const [busy, setBusy] = useState("");
  const [error, setError] = useState("");
  const [tab, setTab] = useState("edit"); // phone: « Modifier » / « Aperçu »

  useEffect(() => { api.get("/knowledge/owner-options").then(({ data }) => setOwners(data.items || [])).catch(() => {}); }, []);
  useEffect(() => {
    if (!articleId) return undefined;
    let alive = true;
    api.get(`/knowledge/articles/${articleId}`).then(({ data }) => {
      if (!alive) return;
      if (!data.draft) { setPhase("nodraft"); return; }
      setSource(data);
      setForm(emptyArticleForm(data));
      setPhase("ready");
    }).catch(() => { if (alive) setPhase("error"); });
    return () => { alive = false; };
  }, [articleId]);

  const set = (patch) => setForm((f) => ({ ...f, ...patch }));
  const problem = useMemo(() => articleProblem(form, { creating: !editing }), [form, editing]);
  const setSection = (i, patch) => set({ sections: form.sections.map((s, j) => (j === i ? { ...s, ...patch } : s)) });
  const move = (i, d) => { const to = i + d; if (to < 0 || to >= form.sections.length) return; const next = [...form.sections]; [next[i], next[to]] = [next[to], next[i]]; set({ sections: next }); };
  const preview = useMemo(() => sectionsPayload(form.sections).map((s, i) => ({ id: s.id || `n${i}`, title: s.title, body: s.body })), [form.sections]);

  const save = async (publish) => {
    setBusy(publish ? "publish" : "save");
    setError("");
    try {
      let data;
      if (editing) {
        ({ data } = await api.put(`/knowledge/articles/${articleId}/draft`, { ...draftPayload(form), expected_version: source.version }));
      } else {
        ({ data } = await api.post("/knowledge/articles", createPayload(form)));
      }
      if (publish) {
        ({ data } = await api.post(`/knowledge/articles/${data.id}/publish`, { expected_version: data.version }));
        toast.success("Article publié");
      } else {
        toast.success(editing ? "Brouillon enregistré" : "Article créé en brouillon");
      }
      navigate(`/support/connaissances/${data.id}`);
    } catch (e) {
      setError(knowledgeError(e, "Enregistrement impossible. Réessayez."));
    } finally {
      setBusy("");
    }
  };

  if (phase === "loading") return <div className="space-y-3"><Skeleton className="h-8 w-64" /><Skeleton className="h-48 w-full" /></div>;
  if (phase === "error" || phase === "nodraft") {
    return (
      <Card className="p-6 text-center space-y-2" data-testid="article-editor-error">
        <p className="text-sm text-slate-700">{phase === "nodraft" ? "Aucun brouillon en cours : ouvrez l'article et choisissez « Nouvelle version »." : "Cet article est introuvable ou vous n'y avez pas accès."}</p>
        <Button asChild variant="outline"><Link to={editing ? `/support/connaissances/${articleId}` : "/support/connaissances"}>Retour</Link></Button>
      </Card>
    );
  }
  const back = editing ? `/support/connaissances/${articleId}` : "/support/connaissances";
  return (
    <div className="space-y-4" data-testid="article-editor">
      <div>
        <Link to={back} className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-900"><ArrowLeft size={14} />Retour</Link>
        <h1 className="font-display font-bold text-2xl text-slate-900 mt-1">{editing ? `Brouillon — ${source.number}` : "Nouvel article"}</h1>
        <p className="text-sm text-slate-500">Écrivez en texte simple : une ligne qui commence par « - » devient une puce, un lien https s'ouvre dans un nouvel onglet, [texte](/page) mène à une page du CRM.</p>
      </div>
      <div className="flex gap-1 lg:hidden" role="tablist" aria-label="Édition">
        {[["edit", "Modifier"], ["preview", "Aperçu"]].map(([id, label]) => (
          <button key={id} type="button" role="tab" aria-selected={tab === id} onClick={() => setTab(id)} data-testid={`editor-tab-${id}`}
            className={`rounded-full px-3 py-1.5 text-sm font-medium ${tab === id ? "bg-jamora text-white" : "bg-white text-slate-600 border border-slate-200"}`}>{label}</button>
        ))}
      </div>
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2 items-start">
        <div className={`space-y-4 min-w-0 ${tab === "preview" ? "hidden lg:block" : ""}`}>
          <Card className="p-4 sm:p-5 space-y-3">
            <div className="space-y-1.5"><Label htmlFor="kb-title">Titre</Label><Input id="kb-title" value={form.title} maxLength={200} onChange={(e) => set({ title: e.target.value })} className="h-10" data-testid="article-title-input" /></div>
            {!editing && (
              <div className="grid gap-3 sm:grid-cols-2">
                <div className="space-y-1.5"><Label>Catégorie</Label>
                  <Select value={form.category || undefined} onValueChange={(category) => set({ category })}>
                    <SelectTrigger className="h-10" aria-label="Catégorie" data-testid="article-category"><SelectValue placeholder="Choisir une catégorie" /></SelectTrigger>
                    <SelectContent>{CATEGORIES.map(([id, label]) => <SelectItem key={id} value={id}>{label}</SelectItem>)}</SelectContent>
                  </Select></div>
                <div className="space-y-1.5"><Label>Propriétaire</Label>
                  <Select value={form.owner_id || undefined} onValueChange={(owner_id) => set({ owner_id })}>
                    <SelectTrigger className="h-10" aria-label="Propriétaire" data-testid="article-owner"><SelectValue placeholder="Choisir une personne" /></SelectTrigger>
                    <SelectContent>{owners.map((o) => <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>)}</SelectContent>
                  </Select></div>
                <div className="space-y-1.5"><Label>Relecture</Label>
                  <Select value={String(form.review_months)} onValueChange={(v) => set({ review_months: Number(v) })}>
                    <SelectTrigger className="h-10" aria-label="Relecture" data-testid="article-period"><SelectValue /></SelectTrigger>
                    <SelectContent>{REVIEW_PERIODS.map(([n, label]) => <SelectItem key={n} value={String(n)}>{label}</SelectItem>)}</SelectContent>
                  </Select></div>
              </div>
            )}
            <div className="space-y-1.5"><Label htmlFor="kb-tags">Mots-clés</Label><Input id="kb-tags" value={form.tags} onChange={(e) => set({ tags: e.target.value })} className="h-10" placeholder="sauvegarde, urgence" data-testid="article-tags" /></div>
          </Card>
          {form.sections.map((s, i) => (
            <Card key={s.id || i} className="p-4 space-y-2" data-testid={`section-edit-${i}`}>
              <div className="flex gap-2">
                <Input value={s.title} maxLength={160} onChange={(e) => setSection(i, { title: e.target.value })} placeholder={`Titre de la section ${i + 1}`} className="h-10" aria-label={`Titre de la section ${i + 1}`} data-testid={`section-title-${i}`} />
                <Button type="button" variant="ghost" className="h-10 px-2" onClick={() => move(i, -1)} disabled={i === 0} aria-label={`Monter la section ${i + 1}`}>↑</Button>
                <Button type="button" variant="ghost" className="h-10 px-2" onClick={() => move(i, 1)} disabled={i === form.sections.length - 1} aria-label={`Descendre la section ${i + 1}`}>↓</Button>
                <Button type="button" variant="ghost" className="h-10 w-10 p-0 shrink-0" onClick={() => set({ sections: form.sections.filter((_, j) => j !== i) })} aria-label={`Retirer la section ${i + 1}`}><Trash2 size={15} /></Button>
              </div>
              <Textarea rows={6} maxLength={8000} value={s.body} onChange={(e) => setSection(i, { body: e.target.value })} aria-label={`Texte de la section ${i + 1}`} data-testid={`section-body-${i}`} />
            </Card>
          ))}
          {form.sections.length < 30 && <Button type="button" variant="outline" onClick={() => set({ sections: [...form.sections, emptySection()] })} data-testid="section-add"><Plus size={14} className="mr-1.5" />Ajouter une section</Button>}
        </div>
        <Card className={`p-4 sm:p-6 min-w-0 lg:sticky lg:top-4 ${tab === "edit" ? "hidden lg:block" : ""}`} data-testid="article-preview">
          <p className="text-xs uppercase tracking-wide text-slate-500 mb-2">Aperçu</p>
          <h2 className="font-display font-bold text-xl text-slate-900 mb-4 break-words">{form.title || "Sans titre"}</h2>
          {preview.length === 0 ? <p className="text-sm text-slate-500">L'aperçu apparaît à mesure que vous écrivez.</p> : <Sections sections={preview} />}
        </Card>
      </div>
      {error && <p className="text-sm text-red-600" role="alert" data-testid="article-editor-submit-error">{error}</p>}
      <div className="flex flex-wrap gap-2 justify-end">
        <Button asChild variant="outline" type="button"><Link to={back}>Annuler</Link></Button>
        <Button variant="outline" onClick={() => save(false)} disabled={!!busy || !!problem} title={problem || undefined} data-testid="article-save">{busy === "save" && <Loader2 size={14} className="mr-1.5 animate-spin" />}Enregistrer le brouillon</Button>
        <Button onClick={() => save(true)} disabled={!!busy || !!problem || preview.length === 0} title={problem || (preview.length === 0 ? "Rédigez au moins une section" : undefined)} data-testid="article-save-publish">{busy === "publish" && <Loader2 size={14} className="mr-1.5 animate-spin" />}Enregistrer et publier</Button>
      </div>
    </div>
  );
}
